import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import FormHelperText from "@mui/material/FormHelperText";

import {
    Box,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Link,
    List,
    ListItem,
    ListItemText,
    Menu,
    SxProps,
    TextField,
} from "@mui/material";
import styled from "styled-components";

import { DataUtilities } from "../../rule-engine-core/Common/DataUtilities";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";
import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";

import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DropdownIcon } from "../Icon/DropdownIcon";
import { ObjectDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/ObjectDataDefinition";
import { SearchGrayIcon } from "../Icon/SearchGrayIcon";
import { StyledButton } from "../BuildlingBlocks/StyledButton";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { UILabel } from "../BuildlingBlocks/UILabel";

export interface IVariablePicker {
    selected: string | null | undefined;
    label?: string;
    hint?: string;
    error?: string;
    requiredType?: DataTypes | ObjectDataDefinition;
    data: DataDefinition;
    allowNewVariable?: boolean;
    onVariableSet: (path: string, def?: IDataDefinition) => void;
    isList?: boolean;
    variant?: "outlined" | "standard" | "filled";
    size?: "small" | "medium" | undefined;
    fullWidth?: boolean;
    style?: CSSProperties;

    anchorEl?: HTMLElement | null;
    renderTitle?: () => JSX.Element;
    hideInput?: boolean;
    startOpenIfEmptyOrInvalid?: boolean;
    notPopup?: boolean;

    open?: boolean;
    onClose?: () => void;
    renderInput?: () => void;

    hideExpectedType?: boolean;
    sx?: SxProps;
    inputSx?: SxProps;
}

export function VariablePicker(props: IVariablePicker) {
    const [showContent, setShowContent] = useState<boolean>(props.open || false);

    const [selected, setSelected] = useState<string | null | undefined>(props.selected);

    const [searchStr, setSearchStr] = useState<string>("");
    const [mode, setMode] = useState<"new" | "pick">(
        props.data.fields.length === 0 && props.allowNewVariable ? "new" : "pick",
    );

    const [value, setValue] = useState<string | null>(null);

    let selectedField: IDataDefinition | null | undefined = useMemo(() => {
        try {
            return DataUtilities.getFieldFromDataDefinitionByName(props.data.fields, selected!) as IDataDefinition;
        } catch (e) {
            return undefined;
        }
    }, [props.data.fields, selected]);

    useEffect(() => {
        setShowContent(props.open || false);
    }, [props.open]);

    const ref = React.useRef<HTMLDivElement>(null);

    let show: boolean = showContent;

    const [newVariable, setNewVariable] = useState<{
        name: string;
        type: DataTypes;
        path: string;
        islist: boolean;
    }>(() => {
        let state = {
            name: "",
            type: DataTypes.STRING,
            path: "",
            islist: props.isList || false,
        };

        if (!selectedField) {
            if (props.selected) {
                state.name = props.selected;
            }

            if (props.requiredType && !(props.requiredType instanceof ObjectDataDefinition)) {
                state.type = props.requiredType;
            }
        }

        return state;
    });

    const error: string | undefined = useMemo(() => {
        if (props.error && props.error) {
            return props.error;
        } else if (selectedField) {
            if (DataUtilities.getTypesMatch(props.data, selected!, props.requiredType, props.isList)) return;
            return "Incorrect variable type";
        } else if (selected && !selectedField) {
            return "Selected field does not exists in fields.";
        }
        return;
    }, [selectedField, selected, props.requiredType, props.error]);

    const filteredFields = DataUtilities.filterFieldsByName(
        props.data.fields,

        value || searchStr,
    ) as IDataDefinition[];

    useEffect(() => {
        setSelected(props.selected);
    }, [props.selected, setSelected]);

    const onClose = () => {
        if (props.onClose) {
            props.onClose();
        } else {
            setShowContent(false);
        }
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
        setNewVariable({
            ...newVariable,
            name: event.target.value,
        });
    };

    const onSelect = (path: string) => {
        setValue(null);
        setSelected(path);
    };

    const onChangeNewVariableName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewVariable({
            ...newVariable,
            name: event.target.value,
        });
    };

    const onChangeNewValueIsList = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewVariable({
            ...newVariable,
            islist: event.target.checked,
        });
    };

    const onChangeNewVariableType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewVariable({
            ...newVariable,
            type: event.target.value as DataTypes,
        });
    };

    const onAddNew = () => {
        if (newVariable && newVariable.name.trim() !== "" && props.allowNewVariable) {
            let def: IDataDefinition | null = null;
            if (typeof props.requiredType === "object") {
                const type = Reflect.get(props.requiredType, "type") as DataTypes;
                def = DataUtilities.createDataByType(type);
                DataUtilities.spreadDeepData(def, props.requiredType);
            } else if (props.requiredType) {
                def = DataUtilities.createDataByType(props.requiredType as DataTypes);
            } else {
                def = DataUtilities.createDataByType(newVariable.type);
            }

            def.name = newVariable.name;
            def.list = newVariable.islist;
            setValue(null);
            setNewVariable({
                name: "",
                type: DataTypes.STRING,
                path: "",
                islist: false,
            });

            setMode("pick");
            onClose();

            props.onVariableSet(newVariable.path, def);
        }
    };

    const renderListFields = (fields: IDataDefinition[], keys: string[] = []) => {
        return (
            <>
                <List>
                    {fields.map((f: IDataDefinition) => {
                        const path = [...keys, f.name || ""].join(".");

                        const available = DataUtilities.getTypesMatch(
                            props.data,
                            path,
                            props.requiredType,
                            props.isList,
                        );
                        if (f.type === DataTypes.OBJECT) {
                            const subfields = Reflect.get(f, "fields") as IDataDefinition[];
                            return (
                                <React.Fragment key={path}>
                                    <ListItem
                                        button
                                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                                            event.stopPropagation();
                                            onSelect(path);
                                        }}
                                        selected={selected === path}
                                        data-testid={`${path}${selected === path ? "-selected" : ""}`}
                                    >
                                        <ListItemText
                                            primary={f.name}
                                            primaryTypographyProps={{
                                                classes: {
                                                    root: available ? "available" : undefined,
                                                },
                                            }}
                                        />
                                    </ListItem>
                                    <NestedListWrapper>
                                        {subfields.length ? renderListFields(subfields, [...keys, f.name || ""]) : null}
                                    </NestedListWrapper>
                                </React.Fragment>
                            );
                        } else {
                            return (
                                <ListItem
                                    selected={selected === path}
                                    key={`${path}`}
                                    button
                                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                                        event.stopPropagation();
                                        onSelect(path);
                                    }}
                                    data-testid={`${path}${selectedField === f ? "-selected" : ""}`}
                                >
                                    <ListItemText
                                        primary={f.name}
                                        primaryTypographyProps={{
                                            classes: {
                                                root: available ? "available" : undefined,
                                            },
                                        }}
                                    />
                                    <ListItemText secondary={f.type} />
                                </ListItem>
                            );
                        }
                    })}
                </List>
            </>
        );
    };

    const renderExistingFields = () => {
        return (
            <>
                <Subheader>
                    <TextField
                        value={searchStr}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setSearchStr(event.target.value || "")
                        }
                        placeholder="Search"
                        data-testid="variable-picker-search"
                        fullWidth
                        classes={{
                            root: "Search_TextField",
                        }}
                        InputProps={{
                            endAdornment: <SearchGrayIcon />,
                        }}
                    />
                </Subheader>

                <ExistingWrapper>
                    {renderListFields(filteredFields, [])}
                    {filteredFields.length === 0 && (
                        <FormHelperText classes={{ root: "VariablePicker-nofieldMessage" }}>
                            There is no fields.
                        </FormHelperText>
                    )}

                    {props.allowNewVariable && (
                        <ButtonGroupDiv marginTop={8}>
                            <Link
                                classes={{
                                    root: "BtnVariablePicker-to-create",
                                }}
                                data-testid="VariablePicker_NewMode"
                                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                                    event.stopPropagation();
                                    setMode("new");
                                }}
                            >
                                Add new
                            </Link>
                        </ButtonGroupDiv>
                    )}
                </ExistingWrapper>
            </>
        );
    };

    const renderButtons = (mode: "new" | "pick", sticky?: boolean) => {
        if (mode === "new")
            return (
                <ButtonGroupDiv
                    marginTop={8}
                    className="VariablePicker-buttonGroups"
                    sticky={sticky}
                >
                    <Link
                        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                            event.stopPropagation();
                            if (props.data.fields.length > 0) {
                                setMode("pick");
                            } else {
                                onClose();
                                props.onVariableSet("");
                            }
                        }}
                    >
                        Cancel
                    </Link>

                    <StyledButton
                        variant="contained"
                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            onAddNew();
                        }}
                        data-testid="BtnCreateVariable"
                    >
                        Create
                    </StyledButton>
                </ButtonGroupDiv>
            );

        return (
            <ButtonGroupDiv
                marginTop={8}
                sticky={sticky}
                className="VariablePicker-buttonGroups"
            >
                <Link
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        event.stopPropagation();
                        props.onVariableSet(props.selected || "");
                        setSelected(props.selected);
                        onClose();
                    }}
                >
                    Cancel
                </Link>

                <StyledButton
                    data-testid="VariablePicker_Select"
                    variant="contained"
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    disabled={!selected || selected === ""}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        props.onVariableSet(selected || "");
                        onClose();
                    }}
                >
                    Choose
                </StyledButton>
            </ButtonGroupDiv>
        );
    };

    const renderNewField = () => (
        <NewFieldDiv>
            <VFormControl>
                <UILabel>Data Name</UILabel>
                <TextField
                    variant="outlined"
                    value={newVariable.name}
                    onChange={onChangeNewVariableName}
                    error={newVariable.name === ""}
                    helperText={newVariable.name === "" ? "name is required" : ""}
                    data-testid="variable-picker-add"
                    onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
                        if (event.key === "Enter") {
                            onAddNew();
                        }
                    }}
                />
            </VFormControl>

            <VFormControl>
                <UILabel>Type</UILabel>
                <TextField
                    select
                    variant="outlined"
                    SelectProps={{ native: true, disabled: !!props.requiredType }}
                    value={newVariable.type!}
                    onChange={onChangeNewVariableType}
                    data-testid="variable-picker-change-type"
                >
                    <option>{DataTypes.STRING}</option>
                    <option>{DataTypes.NUMBER}</option>
                    <option>{DataTypes.BOOLEAN}</option>
                    <option>{DataTypes.DATE}</option>
                    <option>{DataTypes.FILE}</option>
                    <option>{DataTypes.OBJECT}</option>
                    <option>{DataTypes.UNDEFINED}</option>
                </TextField>
            </VFormControl>
            <VFormControl>
                <FormControlLabel
                    data-testid="variable-picker-is-list"
                    control={
                        <Checkbox
                            checked={newVariable.islist}
                            onChange={onChangeNewValueIsList}
                            name="islist"
                        />
                    }
                    label="is list"
                />
            </VFormControl>
        </NewFieldDiv>
    );

    return (
        <VariablePickerDiv
            // className="VariablePicker_root"
            fullWidth={props.fullWidth}
            ref={ref}
            data-testid="VariablePicker_root"
            sx={props.sx}
        >
            {!props.hideInput && (
                <>
                    {props.renderInput ? (
                        props.renderInput()
                    ) : (
                        <TextField
                            size={props.size || "medium"}
                            value={value || selected}
                            onChange={onChange}
                            onFocus={() => {
                                setShowContent(true);
                            }}
                            variant={props.variant || "outlined"}
                            error={!!error}
                            label={props.label}
                            helperText={error ?? props.hint}
                            data-testid={`variable-picker-value${props.requiredType && error ? "-error" : ""}`}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        onClick={() => setShowContent(true)}
                                    >
                                        <DropdownIcon />
                                    </InputAdornment>
                                ),
                                readOnly: !props.allowNewVariable && props.data.fields.length === 0,
                            }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth={props.fullWidth}
                            autoFocus={!!props.startOpenIfEmptyOrInvalid && !selectedField}
                            inputProps={{
                                style: props.style,
                            }}
                            sx={props.inputSx}
                        />
                    )}

                    {(!!error || !props.hideExpectedType) &&
                        (props.isList === true || typeof props.requiredType !== "undefined") && (
                            <ExpectedTypeWrapper
                                className={"expected-type-render"}
                                alwaysShow={!!error}
                                data-testid={`expected-type-render-${props.requiredType}`}
                            >
                                Expected Type:{" "}
                                <span className="type">
                                    {props.requiredType
                                        ? typeof props.requiredType === "string"
                                            ? props.requiredType
                                            : "object data definition"
                                        : ""}
                                    {props.isList ? " list" : ""}
                                </span>
                            </ExpectedTypeWrapper>
                        )}
                </>
            )}
            {props.notPopup ? (
                <VariablePickerContentDiv className="VariablePicker_Content">
                    {props.renderTitle && props.renderTitle()}
                    {mode === "new" ? renderNewField() : renderExistingFields()}
                    {renderButtons(mode)}
                </VariablePickerContentDiv>
            ) : (
                <StyledMenu
                    open={show}
                    anchorEl={props.anchorEl || ref.current}
                    disableEnforceFocus
                    disableRestoreFocus
                    onClose={() => {
                        if (props.selected !== selected && selected) {
                            props.onVariableSet(selected);
                        }
                        onClose();
                    }}
                >
                    <VariablePickerContentDiv className="VariablePicker_Content">
                        {props.renderTitle && props.renderTitle()}
                        {mode === "new" ? renderNewField() : renderExistingFields()}
                    </VariablePickerContentDiv>

                    {renderButtons(mode, true)}
                </StyledMenu>
            )}
        </VariablePickerDiv>
    );
}

export default VariablePicker;

const VariablePickerDiv = styled(Box)<{ fullWidth?: boolean }>`
    position: relative;
    display: inline-block;
    width: ${(props) => (props.fullWidth ? "100%" : "auto")};

    &:hover .expected-type-render {
        opacity: 1;
    }

    .MuiOutlinedInput-notchedOutline {
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 4px;
    }

    .MuiList-root {
        &.MuiList-padding {
            padding-top: 0;
            padding-bottom: 0;
        }

        .MuiListItem-root {
            padding: 5px 12px;
            display: flex;
            justify-content: space-between;
        }

        .MuiListSubheader-gutters {
            padding: 0 12px;
        }

        .MuiListItemText-root {
            flex: inherit;
            margin: 0;

            .MuiTypography-body1 {
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 17px;
                letter-spacing: 0px;
                text-align: left;
            }

            .MuiListItemText-primary {
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                font-weight: 300;

                &.available {
                    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                    font-weight: 700;
                }
            }

            .MuiListItemText-secondary {
                color: ${UIStyleConstants.BLUE_COLOR};
                text-transform: capitalize;
            }
        }
    }
`;

const VariablePickerContentDiv = styled.div`
    padding: 0 10px;

    width: 300px;

    max-width: 100%;

    background: ${UIStyleConstants.WHITE_COLOR};

    display: flex;
    flex-direction: column;

    .MuiInputBase-root {
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0em;
        text-align: left;
    }

    .MuiOutlinedInput-input {
        padding: 6px 12px;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        height: auto;
        background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
    }

    .MuiOutlinedInput-notchedOutline {
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 4px;
    }

    .MuiList-root {
        &.MuiList-padding {
            padding-top: 0;
            padding-bottom: 0;
        }

        .MuiListItem-root {
            padding: 5px 12px;
            display: flex;
            justify-content: space-between;

            &.Mui-selected {
                background: ${UIStyleConstants.BLUE_LIGHTER_COLOR};
            }
        }

        .MuiListSubheader-gutters {
            padding: 0 12px;
            background: ${UIStyleConstants.WHITE_COLOR};
        }

        .MuiListItemText-root {
            flex: inherit;
            margin: 0;

            .MuiTypography-body1 {
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 17px;
                letter-spacing: 0px;
                text-align: left;
            }

            .MuiListItemText-primary {
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                font-weight: 300;

                &.available {
                    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
                    font-weight: 700;
                }
            }

            .MuiListItemText-secondary {
                color: ${UIStyleConstants.BLUE_COLOR};
                text-transform: capitalize;
            }
        }
    }
`;

const NestedListWrapper = styled.div`
    padding-left: 10px;
`;

const NewFieldDiv = styled.div`
    padding-top: 13px;
    display: flex;
    flex-direction: column;
`;

const VFormControl = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 11px;
`;

const ExistingWrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .MuiList-root {
        flex: 1;

        overflow-y: auto;
        overflow-x: hidden;
    }

    .Search_TextField {
        padding: 7px 0 3px;

        .MuiOutlinedInput-input {
            padding: 5px 10px;
        }

        .MuiInputBase-input {
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 20px;
            letter-spacing: 0em;
            text-align: left;
        }
    }

    .VariablePicker-nofieldMessage {
        padding: 0 12px;
    }
`;

const ButtonGroupDiv = styled.div<{ marginTop?: number; sticky?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;

    ${(props) =>
        props.sticky &&
        `
            position: sticky;
            bottom: 0px;
            background: ${UIStyleConstants.WHITE_COLOR};
            padding: 8px 12px;
            
    `}
    ${(props) => (props.marginTop ? `margin-top: ${props.marginTop}px;` : "")}
  .BtnVariablePicker-to-create {
        margin-left: auto;
    }

    .MuiLink-root {
        text-align: center;
    }

    > * {
        margin-left: 10px !important;
    }
`;

const Subheader = styled.div`
    padding: 13px 8px 5px;
    position: sticky;
    top: 0;

    background: ${UIStyleConstants.WHITE_COLOR};

    z-index: 2;
`;

const StyledMenu = styled(Menu)`
    .MuiList-root[role="menu"] {
        padding: 0;
    }
`;

const ExpectedTypeWrapper = styled.div<{ alwaysShow: boolean }>`
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    font-size: 12px;
    margin-left: 14px;
    margin-right: 14px;
    margin-top: 5px;
    line-height: 1;

    ${(props) =>
        !props.alwaysShow &&
        `
            opacity: 0;
        `}
    .type {
        font-weight: 700;
        color: ${UIStyleConstants.BLUE_COLOR};
    }
`;
